import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import type { IBlogAllQueryNode } from "@repo/shopify/queries/blog-gql";
import { extractId } from "@repo/shopify/utils/common";
import type { Video as VideoType } from "@repo/shopifyV2/types/storefront.types";

import Button from "@atoms/Button";
import Dim from "@atoms/Dim";
import Icon from "@atoms/Icon";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";
import Select from "@atoms/Select";
import Video from "@atoms/Video";

import FeaturesCard from "@organisms/FeaturesCard";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useFeatures from "@hooks/useFeatures";
import useHeaderScroll from "@hooks/useHeaderScroll";

import { useLayoutStore } from "@store/layout";

const LOOKBOOK_YEARS = [
  { name: "All", value: "All" },
  { name: "2026", value: "2026" },
  { name: "2025", value: "2025" },
  { name: "2024", value: "2024" },
  { name: "2023", value: "2023" },
  { name: "2022", value: "2022" },
  { name: "2021", value: "2021" },
  { name: "2020", value: "2020" },
  { name: "2019", value: "2019" },
  { name: "2018", value: "2018" },
  { name: "2017", value: "2017" },
  { name: "2016", value: "2016" },
  { name: "2015", value: "2015" },
  { name: "2014", value: "2014" },
];

const FEATURES_OPTIONS = [
  { name: "All Articles", value: "all", url: URLS.FEATURES },
  { name: "News", value: "news", url: URLS.NEWS },
  { name: "Lookbook", value: "lookbook", url: URLS.LOOKBOOK },
  { name: "Videos", value: "videos", url: URLS.VIDEO },
];

export default function Features() {
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const { handle } = useParams();

  const { isPC } = useCheckDevice();
  const { setMeta } = useLayoutStore();

  const [searchParams, setSearchParams] = useSearchParams();

  const videoHandle = searchParams.get("handle");

  const loaderEl = useRef(null);

  const [isLarge, setIsLarge] = useState(true);
  const [year, setYear] = useState(searchParams.get("year") ?? LOOKBOOK_YEARS[0].value);

  const category = useMemo(() => {
    if (pathname === URLS.FEATURES) return "all";
    if (pathname.includes(URLS.LOOKBOOK)) return "lookbook";
    if (pathname.includes(URLS.VIDEO)) return "videos";
    return "news";
  }, [pathname]);

  const featuresOptions = useMemo(() => {
    if (category === "all") return {};
    return {
      blogHandle: category,
      articlesQuery: category === "lookbook" && year !== "All" ? year : undefined,
    };
  }, [category, year]);

  const { blogs, loading, hasNextPage } = useFeatures(loaderEl, featuresOptions);

  useHeaderScroll();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category, year]);

  useEffect(() => {
    setIsLarge(true);

    if (category !== "videos") return;

    document.body.setAttribute("class", "dark");
    document.body.style.background = "#000000";
    return () => {
      document.body.setAttribute("class", "white");
      document.body.style.background = "";
    };
  }, [category]);

  const blogArticles = useMemo(() => {
    if (category === "all") {
      return (
        blogs?.edges
          .flatMap(({ node }) => node)
          .map((item) => item.articles)
          .flatMap((item) => item.edges)
          .sort((prev, curr) => new Date(curr.node.publishedAt).getTime() - new Date(prev.node.publishedAt).getTime()) || []
      );
    }

    return blogs?.edges.find(({ node }) => node.handle === category)?.node?.articles?.edges || [];
  }, [blogs, category]);

  /** videos 상세 경로(/features/videos/:handle)일 때 모달로 띄울 영상 */
  const selectedVideo = useMemo(() => {
    if (category === "all") {
      if (!videoHandle) return;
      return blogArticles.find(({ node }) => extractId(node.handle) === videoHandle);
    }

    if (category === "videos") {
      if (!handle) return;
      return blogArticles.find(({ node }) => extractId(node.handle) === handle);
    }
  }, [category, blogArticles, handle, videoHandle]);

  const videoSource = useMemo(
    () => ((selectedVideo?.node as IBlogAllQueryNode)?.video?.reference as VideoType)?.sources?.find((item) => item.format === "mp4"),
    [selectedVideo],
  );

  // true: 가로가 길고 / false: 세로가 김
  const isHorizontal = useMemo(
    () => (selectedVideo?.node?.image?.width || 1920) >= (selectedVideo?.node?.image?.height || 1080),
    [selectedVideo],
  );

  useEffect(() => {
    if (!selectedVideo) return;

    setMeta({ title: `${selectedVideo.node.title} - ${DEFAULT_TITLE}` });
  }, [selectedVideo, setMeta]);

  const handleToggleGrid = () => {
    setIsLarge(!isLarge);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pc:col-span-12 tnt-gap-x pc:grid-cols-12 col-span-8 grid grid-cols-subgrid">
      <div className="pc:col-span-2 pc:tnt-gap-x pc:top-30 sticky top-0 z-10 col-span-8 grid h-fit grid-cols-2" id="filter">
        {isPC ? (
          <>
            {/* 카테고리 네비 */}
            <div className="flex flex-col gap-10">
              <div className="pc:col-span-1 pc:flex-col col-span-8 flex gap-1">
                {FEATURES_OPTIONS?.map(({ value, name, url }) => (
                  <Link as="text" className="capitalize" href={url} isActive={pathname === url} key={value}>
                    {name}
                  </Link>
                ))}
              </div>
              <span className="font-normal!">{blogArticles.length} Results</span>
            </div>

            <div className="col-span-1 flex flex-col gap-1">
              {category === "all" ? (
                <>
                  <Button as="text" isActive={isLarge} onClick={handleToggleGrid}>
                    View Large
                  </Button>
                  <Button as="text" isActive={!isLarge} onClick={handleToggleGrid}>
                    View Small
                  </Button>
                </>
              ) : null}

              {/*{category === "news" ? <Button as="text">All</Button> : null}*/}

              {category === "lookbook" ? (
                <div className="pc:flex-col pc:pr-0 flex flex-row gap-y-1 pr-4">
                  {LOOKBOOK_YEARS.map((tag) => (
                    <Button
                      as="text"
                      isActive={year === tag.value}
                      key={tag.value}
                      onClick={() => {
                        setSearchParams({ year: tag.value });
                        setYear(tag.value);
                      }}
                    >
                      {tag.value}
                    </Button>
                  ))}
                </div>
              ) : null}

              {/*{category === "videos" ? <Button as="text">All</Button> : null}*/}
            </div>
          </>
        ) : (
          <div className="col-span-8 flex w-full justify-between gap-1 bg-white py-2.5 dark:bg-black">
            <div className="flex items-center gap-1">
              <span className="text-base!">Viewing: </span>
              <Select
                as="square"
                id="features"
                name="features"
                onChange={(e) => {
                  navigate(e.target.value === "all" ? "/features" : `/features/${e.target.value.toLowerCase()}`);
                }}
                options={FEATURES_OPTIONS}
                value={category}
                variant="secondary"
              />
            </div>
            {category === "all" && (
              <Button as="wrapper" className="items-center" isActive={isLarge} onClick={handleToggleGrid}>
                <Icon className="size-4.5" name={isLarge ? "GridSmall" : "GridLarge"} />
              </Button>
            )}

            {category === "lookbook" && (
              <Select
                as="square"
                id="features"
                name="features"
                onChange={(e) => {
                  setSearchParams({ year: e.target.value });
                  setYear(e.target.value);
                }}
                options={LOOKBOOK_YEARS}
                value={year}
                variant="secondary"
              />
            )}
          </div>
        )}
      </div>

      {/* 목록 */}
      {!blogArticles?.length && loading ? (
        <Loading type="page" />
      ) : (
        <>
          <section
            className={classNames(
              "pc:col-span-10 pc:grid col-span-8 gap-x-5",
              category === "all" && "pc:grid-cols-3 flex flex-col gap-y-20",
              category === "news" && [
                "gap-y-20",
                isLarge ? "flex grid-cols-3 flex-col" : "tnt-gap-x pc:grid-cols-4 pc:gap-y-10 flex flex-col gap-y-5",
              ],
              category === "lookbook" && "pc:grid-cols-2 flex flex-col gap-y-20",
              category === "videos" && "pc:col-span-10 pc:grid-cols-5 pc:gap-y-10 grid grid-cols-1 gap-20",
            )}
          >
            {blogArticles.map(({ node }: { node: IBlogAllQueryNode }) => (
              <FeaturesCard category={category} isLarge={isLarge} key={node.id} node={node} />
            ))}
          </section>

          {selectedVideo ? (
            <>
              <div className="pc:px-5 pc:h-fit pc:bg-transparent fixed top-0 left-0 z-80 mx-auto flex w-full items-end justify-between bg-black p-4 text-white">
                <Link className="text-title font-features-normal flex w-fit leading-none" href={URLS.HOME}>
                  thisisneverthat®
                </Link>
                <Button
                  as="text"
                  className="text-white"
                  onClick={() => {
                    if (category === "videos") navigate(URLS.VIDEO);
                    if (category === "all") setSearchParams();
                  }}
                >
                  {isPC ? "Close" : <Icon className="size-4.5" name="Close" />}
                </Button>
              </div>

              <Dim
                className="z-20 bg-black!"
                onClick={() => {
                  if (category === "videos") navigate(URLS.VIDEO);
                  if (category === "all") setSearchParams();
                }}
              />
              <div
                className={classNames(
                  "video-frame fixed top-1/2 left-1/2 z-70 -translate-x-1/2 -translate-y-1/2",
                  // isHorizontal
                  //   ? "pc:w-col-span-10 w-[calc(100%-2rem)]"
                  //   : "pc:w-col-span-4 max-h-[calc(100%-8rem)] w-[calc(100%-2rem)]",
                )}
                // style={{
                //   aspectRatio: `${selectedVideo.node?.image?.width} / ${selectedVideo.node?.image?.height}`,
                // }}
              >
                {videoSource ? (
                  <Video height={videoSource?.height || 1800} src={videoSource?.url || ""} width={videoSource?.width || 1440} />
                ) : (
                  <Image
                    alt={selectedVideo.node.image?.altText || ""}
                    className="object-contain"
                    height={selectedVideo.node.image?.height || 500}
                    src={selectedVideo.node.image?.url || ""}
                    width={selectedVideo.node.image?.width || 400}
                  />
                )}
              </div>
            </>
          ) : null}

          <div className="pc:col-span-12 col-span-8" ref={loaderEl} />

          {hasNextPage ? <Loading /> : null}
          {(category === "all" || category === "news" || (category === "lookbook" && year === "All") || category === "videos") && (
            <>
              {blogArticles?.length && !hasNextPage ? (
                <Button
                  as="text"
                  className="pc:col-span-12 col-span-8 mt-40 flex place-self-center"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  Back to Top
                </Button>
              ) : null}
            </>
          )}
        </>
      )}
    </div>
  );
}
